import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
/**
 * ContentEditable textarea with @mention support.
 *
 * The value prop contains raw format with @(uuid).
 * The editor renders mentions as styled, non-editable spans.
 * The dropdown is rendered in a portal with smart positioning.
 */
export default function MentionTextarea({
    value,
    onChange,
    profiles = [],
    profilesMap = {},
    placeholder = '',
}) {
    const editorRef = useRef(null)
    const dropdownRef = useRef(null)
    const [showDropdown, setShowDropdown] = useState(false)
    const [dropdownResults, setDropdownResults] = useState([])
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [dropdownStyle, setDropdownStyle] = useState({})
    const isComposing = useRef(false)

    // Convert raw value to HTML for the contentEditable
    const rawToHtml = useCallback(
        (raw) => {
            if (!raw) return ''
            const escaped = raw
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')

            return escaped.replace(
                /@\(([0-9a-f-]{36})\)/g,
                (match, uuid) => {
                    const profile = profilesMap[uuid]
                    const name = profile?.name || 'Utilisateur supprimé'
                    return `<span contenteditable="false" data-mention-id="${uuid}" class="inline-block bg-blue-100 text-blue-700 rounded-sm px-1 mx-0.5 text-sm font-medium select-all">@${name}</span>`
                }
            )
        },
        [profilesMap]
    )

    // Extract raw value from the contentEditable DOM
    const htmlToRaw = useCallback(() => {
        const editor = editorRef.current
        if (!editor) return ''

        let result = ''
        const walk = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                result += node.textContent
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                if (node.dataset?.mentionId) {
                    result += `@(${node.dataset.mentionId})`
                } else if (node.tagName === 'BR') {
                    result += '\n'
                } else if (
                    node.tagName === 'DIV' &&
                    result.length > 0 &&
                    !result.endsWith('\n')
                ) {
                    result += '\n'
                    node.childNodes.forEach(walk)
                } else {
                    node.childNodes.forEach(walk)
                }
            }
        }

        editor.childNodes.forEach(walk)
        return result
    }, [])

    // Sync HTML when value changes externally
    const lastRawRef = useRef(value)
    useEffect(() => {
        const editor = editorRef.current
        if (!editor) return

        const currentRaw = htmlToRaw()
        if (value !== currentRaw) {
            const html = rawToHtml(value)
            editor.innerHTML = html
            lastRawRef.current = value
        }
    }, [value, rawToHtml, htmlToRaw])

    // Compute dropdown position in fixed coordinates with smart placement
    const computeDropdownPosition = () => {
        const selection = window.getSelection()
        if (!selection.rangeCount) return

        const range = selection.getRangeAt(0).cloneRange()
        range.collapse(true)
        const caretRect = range.getClientRects()[0]
        if (!caretRect) return

        const dropdownHeight = 280 // max-h estimate
        const dropdownWidth = 280
        const gap = 6

        const spaceBelow = window.innerHeight - caretRect.bottom - gap
        const spaceAbove = caretRect.top - gap

        const placeAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

        let left = caretRect.left
        if (left + dropdownWidth > window.innerWidth - 16) {
            left = window.innerWidth - dropdownWidth - 16
        }
        if (left < 16) left = 16

        setDropdownStyle({
            position: 'fixed',
            left,
            ...(placeAbove
                ? { bottom: window.innerHeight - caretRect.top + gap }
                : { top: caretRect.bottom + gap }),
            width: dropdownWidth,
            zIndex: 9999,
        })
    }

    // Handle input
    const handleInput = () => {
        if (isComposing.current) return

        const editor = editorRef.current
        let raw = htmlToRaw()

        // Clear residual <br> so :empty pseudo-class works for placeholder
        if (!raw.trim() && editor) {
            editor.innerHTML = ''
            raw = ''
        }

        lastRawRef.current = raw
        onChange(raw)

        detectMention()
    }

    // Detect @ mention in progress
    const detectMention = () => {
        const selection = window.getSelection()
        if (!selection.rangeCount) return

        const range = selection.getRangeAt(0)
        const node = range.startContainer

        if (node.nodeType !== Node.TEXT_NODE) {
            setShowDropdown(false)
            return
        }

        const textBefore = node.textContent.slice(0, range.startOffset)
        const match = textBefore.match(/@([^\s@]*)$/)

        if (match) {
            const query = match[1].toLowerCase()
            const filtered = profiles.filter(
                (p) =>
                    p.name?.toLowerCase().includes(query) ||
                    p.short?.toLowerCase().includes(query)
            )

            computeDropdownPosition()

            setDropdownResults(filtered.slice(0, 30))
            setSelectedIndex(0)
            setShowDropdown(filtered.length > 0)
        } else {
            setShowDropdown(false)
        }
    }

    // Insert a mention span at the current cursor position
    const insertMention = (profile) => {
        const selection = window.getSelection()
        if (!selection.rangeCount) return

        const range = selection.getRangeAt(0)
        const node = range.startContainer

        if (node.nodeType !== Node.TEXT_NODE) return

        const textBefore = node.textContent.slice(0, range.startOffset)
        const match = textBefore.match(/@([^\s@]*)$/)
        if (!match) return

        const atPos = range.startOffset - match[0].length
        const afterText = node.textContent.slice(range.startOffset)
        const beforeText = node.textContent.slice(0, atPos)

        const span = document.createElement('span')
        span.contentEditable = 'false'
        span.dataset.mentionId = profile.id
        span.className =
            'inline-block bg-blue-100 text-blue-700 rounded-sm px-1 mx-0.5 text-sm font-medium select-all'
        span.textContent = `@${profile.name}`

        const beforeNode = document.createTextNode(beforeText)
        const spaceAfter = document.createTextNode(' ' + afterText)

        const parent = node.parentNode
        parent.insertBefore(beforeNode, node)
        parent.insertBefore(span, node)
        parent.insertBefore(spaceAfter, node)
        parent.removeChild(node)

        const newRange = document.createRange()
        newRange.setStart(spaceAfter, 1)
        newRange.collapse(true)
        selection.removeAllRanges()
        selection.addRange(newRange)

        setShowDropdown(false)

        const raw = htmlToRaw()
        lastRawRef.current = raw
        onChange(raw)
    }

    const handleKeyDown = (e) => {
        if (showDropdown) {
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setSelectedIndex((i) =>
                    Math.min(i + 1, dropdownResults.length - 1)
                )
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setSelectedIndex((i) => Math.max(i - 1, 0))
            } else if (e.key === 'Enter') {
                if (dropdownResults[selectedIndex]) {
                    e.preventDefault()
                    insertMention(dropdownResults[selectedIndex])
                }
            } else if (e.key === 'Escape') {
                setShowDropdown(false)
            }
        }

        // Delete mention span on Backspace
        if (e.key === 'Backspace') {
            const selection = window.getSelection()
            if (!selection.rangeCount) return

            const range = selection.getRangeAt(0)
            if (!range.collapsed) return

            const node = range.startContainer

            if (node.nodeType === Node.TEXT_NODE) {
                const offset = range.startOffset
                const prev = node.previousSibling

                // Cursor at start of text node, previous sibling is a mention — delete mention
                if (offset === 0 && prev?.dataset?.mentionId) {
                    e.preventDefault()
                    prev.remove()
                    handleInput()
                    return
                }
            }

            // Cursor inside the editor directly (not in a text node) — check child before cursor
            if (node === editorRef.current && range.startOffset > 0) {
                const prev = node.childNodes[range.startOffset - 1]
                if (prev?.dataset?.mentionId) {
                    e.preventDefault()
                    prev.remove()
                    handleInput()
                }
            }
        }
    }

    // Scroll selected dropdown item into view
    useEffect(() => {
        if (showDropdown && dropdownRef.current) {
            const item = dropdownRef.current.children[selectedIndex]
            item?.scrollIntoView({ block: 'nearest' })
        }
    }, [selectedIndex, showDropdown])

    // Close dropdown on scroll outside (but not scroll inside the dropdown)
    useEffect(() => {
        if (!showDropdown) return

        const handleClose = (e) => {
            if (dropdownRef.current?.contains(e.target)) return
            setShowDropdown(false)
        }
        window.addEventListener('scroll', handleClose, true)
        return () => window.removeEventListener('scroll', handleClose, true)
    }, [showDropdown])

    const dropdown =
        showDropdown &&
        createPortal(
            <div
                ref={dropdownRef}
                className="bg-white rounded-lg shadow-lg border border-gray-200 max-h-[200px] overflow-y-auto py-0.5"
                style={dropdownStyle}
            >
                {dropdownResults.map((profile, i) => (
                    <div
                        key={profile.id}
                        className={`px-3 py-1.5 text-sm text-gray-700 cursor-pointer truncate ${
                            i === selectedIndex
                                ? 'bg-gray-100'
                                : 'hover:bg-gray-50'
                        }`}
                        onMouseDown={(e) => {
                            e.preventDefault()
                            insertMention(profile)
                        }}
                        onMouseEnter={() => setSelectedIndex(i)}
                    >
                        {profile.name}
                    </div>
                ))}
            </div>,
            document.body
        )

    return (
        <>
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onCompositionStart={() => (isComposing.current = true)}
                onCompositionEnd={() => {
                    isComposing.current = false
                    handleInput()
                }}
                data-placeholder={placeholder}
                className="w-full text-sm outline-hidden overflow-y-auto whitespace-pre-wrap wrap-break-word empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 empty:before:pointer-events-none"
                style={{ minHeight: '1.25rem', maxHeight: '8rem' }}
            />
            {dropdown}
        </>
    )
}
